import { create } from "zustand";
import { persist } from "zustand/middleware";
import { AuthState } from "@/types";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.facetech.my";
const CLIENT_KEY =
  import.meta.env.VITE_CLIENT_KEY || "client_mcvfwllv_ca130d82";

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function checkInternalAdmin(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const portalKey = payload.portal_client_key as string | undefined;
  const orgKey = payload.org_client_key as string | undefined;
  return !!(portalKey && orgKey && portalKey === orgKey);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get): AuthState => ({
      user: null,
      token: null,
      isAuthenticated: false,
      clientKey: null,

      login: async (email: string, password: string) => {
        try {
          const response = await fetch(`${API_BASE_URL}/clients/auth/login`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Client-Key": CLIENT_KEY,
            },
            body: JSON.stringify({
              email,
              password,
              client_key: CLIENT_KEY,
            }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "Login failed");
          }

          const data = await response.json();

          if (data.success && data.token) {
            // Store the organization's client key for API requests
            const orgClientKey =
              data.user.org_client_key || data.user.clientKey;
            const isInternalAdmin = checkInternalAdmin(data.token);

            set({
              user: {
                id: data.user.id,
                email: data.user.email,
                name: data.user.name,
                clientId: data.user.clientId,
                clientName: data.user.clientName,
                last_login: data.user.last_login,
                is_active: data.user.is_active,
                subscriptionType: data.user.subscriptionType,
                portalConfig: data.user.portalConfig,
                isInternalAdmin,
              },
              token: data.token,
              clientKey: orgClientKey,
              isAuthenticated: true,
            });
          } else {
            throw new Error("Invalid response format");
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error("Login error:", error);
          }
          throw error instanceof Error ? error : new Error("Login failed");
        }
      },

      logout: async () => {
        try {
          const { token } = get();
          if (token) {
            await fetch(`${API_BASE_URL}/clients/auth/logout`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                "X-Client-Key": CLIENT_KEY,
              },
            });
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error("Logout error:", error);
          }
        } finally {
          set({
            user: null,
            token: null,
            clientKey: null,
            isAuthenticated: false,
          });
        }
      },

      refreshUser: async () => {
        try {
          const { token } = get();
          if (!token) {
            throw new Error("No token available");
          }

          const response = await fetch(`${API_BASE_URL}/clients/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Client-Key": CLIENT_KEY,
            },
          });

          if (!response.ok) {
            throw new Error("Failed to refresh user data");
          }

          const data = await response.json();

          if (data.success && data.user) {
            const { token: currentToken } = get();
            const isInternalAdmin = currentToken
              ? checkInternalAdmin(currentToken)
              : false;
            set({
              user: {
                id: data.user.id,
                email: data.user.email,
                name: data.user.name,
                clientId: data.user.clientId,
                clientName: data.user.clientName,
                last_login: data.user.last_login,
                is_active: data.user.is_active,
                subscriptionType: data.user.subscriptionType,
                portalConfig: data.user.portalConfig,
                isInternalAdmin,
              },
            });
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error("Refresh user error:", error);
          }
          // If refresh fails, logout the user
          set({
            user: null,
            token: null,
            clientKey: null,
            isAuthenticated: false,
          });
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state: AuthState) => ({
        user: state.user,
        token: state.token,
        clientKey: state.clientKey,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
